import * as React from 'react'

import {
  HorizontalAlignment,
  VerticalAlignment,
} from '@thg-commerce/enterprise-core'
import {
  EnterpriseThemeInterface,
  mq,
  withTheme,
} from '@thg-commerce/enterprise-theme'
import { ButtonEmphases } from '@thg-commerce/gravity-elements/Button/Button'
import { withPrefetch } from '@thg-commerce/gravity-system/prefetch'

import {
  ButtonsContainer,
  Container,
  ImageCollectionWrapper,
  ImageItem,
  ImageOverlayGridItem,
  ImageOverlaySubtitle,
  ImageOverlayTitle,
  ItemContainer,
  StyledButton,
  StyledGrid,
  StyledGridItem,
  StyledImage,
  StyledLink,
  Subtitle,
  TextContainer,
  TextGrid,
  TextGridItem,
  Title,
} from './styles'

interface ItemAlignmentProps {
  vertical: VerticalAlignment
  horizontal: HorizontalAlignment
}

interface ImageOverlayCollectionItemProps {
  image: {
    urls: {
      small: string
      medium: string
      large: string
    }
    alt?: string
    link?: string
  }
  title?: string
  subtitle?: string
  textColor?: 'light' | 'dark'
  itemAlignment?: ItemAlignmentProps
  cta?: { text: string; link: string; emphasis?: ButtonEmphases }
}

export interface ImageOverlayCollectionProps {
  title?: string
  subtitle?: string
  textColor?: 'light' | 'dark'
  horizontalAlignment?: HorizontalAlignment
  itemAlignment?: ItemAlignmentProps
  items: [
    ImageOverlayCollectionItemProps,
    ImageOverlayCollectionItemProps,
    ...ImageOverlayCollectionItemProps[]
  ]
}

const PrefetchStyledLink = withPrefetch('href', StyledLink)

const ImageOverlayItem = withTheme(
  (
    props: ImageOverlayCollectionItemProps & {
      theme: EnterpriseThemeInterface
    },
  ) => {
    const picture = (
      <picture>
        <source
          media={mq(props.theme.breakpointUtils.map, 'lg', true)}
          srcSet={props.image.urls.large}
        />
        <source
          media={mq(props.theme.breakpointUtils.map, 'sm', true)}
          srcSet={props.image.urls.medium}
        />
        <StyledImage
          src={props.image.urls.small}
          alt={props.image.alt || ''}
          width={600}
          height={600}
        />
      </picture>
    )

    const shouldRenderCta = props.cta && props.cta.link && props.cta.text
    const ctaButton = shouldRenderCta && (
      <StyledButton
        renderedAs="a"
        emphasis={props.cta?.emphasis || 'high'}
        href={props.cta?.link}
      >
        {props.cta?.text}
      </StyledButton>
    )

    const useImageLink = !!props.image.link && !shouldRenderCta
    const centerAlignment = props.itemAlignment?.horizontal === 'center'

    return (
      <ItemContainer data-testid="image-rkp" rows={2} columns={1} rowGap={0}>
        <ImageItem rowStart={1} colStart={1} colSpan={1} rowSpan={2}>
          {useImageLink ? (
            <PrefetchStyledLink href={props.image.link}>
              {picture}
            </PrefetchStyledLink>
          ) : (
            picture
          )}
        </ImageItem>
        {(props.subtitle || props.title) && (
          <TextContainer
            rowStart={1}
            colStart={1}
            colSpan={1}
            rowSpan={2}
            verticalAlignment={props.itemAlignment?.vertical}
            removePointerEvents={useImageLink}
          >
            <TextGrid columns={1}>
              <TextGridItem
                horizontalAlignment={props.itemAlignment?.horizontal}
              >
                {props.title && (
                  <ImageOverlayTitle
                    centerText={centerAlignment}
                    lightText={props.textColor === 'light'}
                  >
                    {props.title}
                  </ImageOverlayTitle>
                )}
                {props.subtitle && (
                  <ImageOverlaySubtitle
                    lightText={props.textColor === 'light'}
                    removeMargin={useImageLink}
                  >
                    {props.subtitle}
                  </ImageOverlaySubtitle>
                )}
                {ctaButton && (
                  <ButtonsContainer centerText={centerAlignment}>
                    {ctaButton}
                  </ButtonsContainer>
                )}
              </TextGridItem>
            </TextGrid>
          </TextContainer>
        )}
      </ItemContainer>
    )
  },
)

export const ImageOverlayCollection = ({
  title,
  subtitle,
  items,
  textColor,
  itemAlignment,
  horizontalAlignment,
}: ImageOverlayCollectionProps) => {
  const oddNumGridItem = items.length % 2 === 1
  const colStart = [
    { left: 1, center: 3, right: 5 },
    { left: 1, center: 4, right: 7 },
  ]
  const imageItemsMap = items.map(
    (item, index) =>
      item && (
        <ImageOverlayGridItem
          key={index}
          colSpan={[12, 6, 6, 6]}
          colStart={
            horizontalAlignment && oddNumGridItem && !items[index + 1]
              ? [
                  1,
                  colStart[1][horizontalAlignment],
                  colStart[1][horizontalAlignment],
                  colStart[1][horizontalAlignment],
                ]
              : undefined
          }
        >
          <ImageOverlayItem
            textColor={textColor}
            itemAlignment={itemAlignment}
            {...item}
          />
        </ImageOverlayGridItem>
      ),
  )
  return (
    <Container horizontalAlignment={horizontalAlignment}>
      <StyledGrid columns={12}>
        <StyledGridItem
          colSpan={[12, 8, 6, 6]}
          colStart={
            horizontalAlignment
              ? [
                  1,
                  colStart[0][horizontalAlignment],
                  colStart[1][horizontalAlignment],
                  colStart[1][horizontalAlignment],
                ]
              : 1
          }
        >
          {title && <Title>{title}</Title>}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </StyledGridItem>
      </StyledGrid>
      <ImageCollectionWrapper>{imageItemsMap}</ImageCollectionWrapper>
    </Container>
  )
}
